import { Alerta, Badge, Button, Card, Desplegable, Dialog, ListaOrdenable, Row, Stack, TextField } from "@/components";
import type { NodoCategoria } from "@/lib/cotizaciones";
import type { EdicionCategorias } from "./useEdicionCategorias";

interface Props {
  e: EdicionCategorias;
  categorias: NodoCategoria[];
  abierta: (clave: string) => boolean;
  alternar: (clave: string, abierta: boolean) => void;
}

/** El árbol en modo edición: el orden de las categorías y, en cada una, sus subcategorías, con agregar, renombrar y borrar. */
export function EdicionArbol({ e, categorias, abierta, alternar }: Props) {
  return (
    <Stack espacio={4}>
      <Alerta tono="alerta" texto={e.aviso} />
      <Alerta texto={e.errorOrden} />
      <Card>
        <Stack espacio={3}>
          <Row justificar="entre" envolver espacio={2}>
            <h2 className="text-titulo font-light tracking-tight">Categorías</h2>
            <Button variante="secundario" tamanio="sm" onClick={e.abrirNuevaCategoria}>
              Agregar categoría
            </Button>
          </Row>
          <p className="text-chico text-texto-suave">Arrastrá cada una desde su agarre o usá las flechas. El número sale de este orden (el mismo de Obra → Presupuesto y el cronograma); se guarda solo.</p>
          <ListaOrdenable
            etiqueta="Orden de las categorías"
            elementos={categorias.map((c) => ({
              id: c.id,
              texto: `${c.numero} ${c.nombre}`,
              contenido: (
                <Row justificar="entre" envolver espacio={2}>
                  <Row envolver espacio={2}>
                    <span>
                      <span className="tabular-nums text-texto-suave">{c.numero}</span> {c.nombre}
                    </span>
                    {!c.activo && <Badge>Archivada</Badge>}
                  </Row>
                  <Row espacio={1}>
                    <Button variante="fantasma" tamanio="sm" aria-label={`Renombrar ${c.numero} ${c.nombre}`} onClick={() => e.abrirRenombrarCategoria(c)}>
                      Renombrar
                    </Button>
                    {c.subcategorias.length === 0 && (
                      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${c.numero} ${c.nombre}`} onClick={() => e.abrirBorrarCategoria(c)}>
                        Borrar
                      </Button>
                    )}
                  </Row>
                </Row>
              ),
            }))}
            onMover={e.moverCategorias}
          />
        </Stack>
      </Card>

      <Stack espacio={2}>
        <h2 className="text-titulo font-light tracking-tight">Subcategorías</h2>
        <p className="text-chico text-texto-suave">Abrí una categoría para agregar, renombrar, mover o borrar sus subcategorías. Para borrar una categoría, primero borrá sus subcategorías; una subcategoría con presupuestos no se borra: mové antes sus presupuestos.</p>
        {categorias.map((c) => {
          const clave = `e${c.id}`;
          return (
            <Desplegable key={c.id} titulo={`${c.numero} ${c.nombre}`} abiertoInicial={abierta(clave)} onCambio={(a) => alternar(clave, a)}>
              {abierta(clave) && (
                <Stack espacio={3}>
                  {c.subcategorias.length === 0 ? (
                    <p className="text-chico text-texto-suave">Todavía no tiene subcategorías.</p>
                  ) : (
                    <ListaOrdenable
                      etiqueta={`Subcategorías de ${c.nombre}`}
                      elementos={c.subcategorias.map((s) => ({
                        id: s.id,
                        texto: `${s.numero} ${s.descripcion}`,
                        contenido: (
                          <Row justificar="entre" envolver espacio={2}>
                            <span className="min-w-0 break-words">
                              <span className="tabular-nums text-texto-suave">{s.numero}</span> {s.descripcion}
                            </span>
                            <Row espacio={1}>
                              <Button variante="fantasma" tamanio="sm" aria-label={`Renombrar ${s.numero} ${s.descripcion}`} onClick={() => e.abrirRenombrarSubcategoria(c, s)}>
                                Renombrar
                              </Button>
                              <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${s.numero} ${s.descripcion}`} onClick={() => e.abrirBorrarSubcategoria(s)}>
                                Borrar
                              </Button>
                            </Row>
                          </Row>
                        ),
                      }))}
                      onMover={(ids) => e.moverSubcategorias(c.id, ids)}
                    />
                  )}
                  <div>
                    <Button variante="secundario" tamanio="sm" aria-label={`Agregar subcategoría a ${c.nombre}`} onClick={() => e.abrirNuevaSubcategoria(c)}>
                      Agregar subcategoría
                    </Button>
                  </div>
                </Stack>
              )}
            </Desplegable>
          );
        })}
      </Stack>
    </Stack>
  );
}

/** Los diálogos de la edición del árbol: el nombre (alta y renombrar) y borrar. */
export function DialogosCategorias({ e }: { e: EdicionCategorias }) {
  const d = e.dialogo;
  return (
    <>
      <Dialog abierto={d?.tipo === "nombre"} onCambio={(abierto) => !abierto && e.cerrar()} titulo={e.tituloNombre}>
        <form onSubmit={(ev) => void e.guardar(ev)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Nombre" value={e.nombre} onChange={(ev) => e.setNombre(ev.target.value)} error={e.errorNombre} />
            <Alerta texto={e.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={e.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={e.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && e.cerrar()} titulo={d?.tipo === "borrar" && d.que === "categoria" ? "Borrar categoría" : "Borrar subcategoría"}>
        <Stack espacio={4}>
          <p>{e.mensajeBorrar}</p>
          <Alerta texto={e.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={e.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={e.borrando} onClick={() => void e.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </>
  );
}
